// "PDF" and "Email" buttons for a booked order. Used on the order-booked card and on the
// activity-form page that opens right after the order (so the buttons are not lost there).
import { useState } from "react";
import { Download, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { authenticatedDownload } from "@/components/bulk/useBulkJob";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function OrderShareButtons({ order }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  if (!order?.id) return null;

  const downloadPdf = async () => {
    // A plain browser link carries no login token, so the PDF is fetched as an authenticated file.
    setPdfBusy(true);
    try {
      await authenticatedDownload(`/orders/${order.id}/pdf`, `${order.order_no || "order"}.pdf`);
    } catch (e) {
      toast.error(`PDF download failed: ${e?.message || e}`);
    } finally { setPdfBusy(false); }
  };

  const submitShare = async () => {
    setShareBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/share`, { channel: "email", recipient_email: shareEmail || undefined });
      if (data.sent) {
        toast.success(`Email sent to ${data.recipient_email}`);
        setShareOpen(false);
      } else {
        toast.error(data.message || "The email could not be sent.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setShareBusy(false); }
  };

  return (
    <>
      <div className="flex gap-2">
        <Button onClick={downloadPdf} variant="outline" disabled={pdfBusy}
                className="flex-1 h-10 rounded-lg" data-testid="secord-download-pdf">
          {pdfBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          PDF
        </Button>
        <Button onClick={() => setShareOpen(true)} className="flex-1 h-10 rounded-lg tulsi-gradient text-white"
                data-testid="secord-share">
          <Mail className="h-4 w-4 mr-2" />Email
        </Button>
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-sm" data-testid="secord-share-dialog">
          <DialogHeader>
            <DialogTitle>Share order by email</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-neutral-500 mb-1">
            The order PDF (with the activity details and photos) is attached to the email.
          </div>
          <Input placeholder="recipient@example.com (leave blank for retailer/SE)"
                 value={shareEmail} onChange={(e) => setShareEmail(e.target.value)}
                 data-testid="secord-share-email" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareOpen(false)}>Cancel</Button>
            <Button onClick={submitShare} disabled={shareBusy} className="tulsi-gradient text-white"
                    data-testid="secord-share-submit">
              {shareBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Send email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
